<!-- eslint-disable @typescript-eslint/no-non-null-assertion -->
<template>
	<AmeliproCard no-card-header>
		<v-row>
			<v-col
				v-if="!rechercheDpa && utilisateurExterneInfo.isOfficine"
				cols="12"
				sm="6"
				md="4"
				lg="2"
			>
				<AmeliproTextField
					id="raisonSociale"
					v-model="raisonSociale"
					bg-white
					label="Raison Sociale"
					clearable
					@change="onFilterChange"
				/>
			</v-col>
			<v-col
				cols="12"
				sm="6"
				md="4"
				lg="2"
			>
				<AmeliproTextField
					id="afficherDateDebut"
					:key="dateDebutKey"
					v-model="afficherDateDebut"
					unique-id="afficherDateDebut"
					type="date"
					label="Dates de garde entre le"
					:max-date="afficherDateFinComputed"
					clearable
					:rules="dateRules"
					@input="onDateChange"
					@change="onFilterChange"
				/>
			</v-col>
			<v-col
				cols="12"
				sm="6"
				md="4"
				lg="2"
			>
				<AmeliproTextField
					id="afficherDateFin"
					:key="dateFinKey"
					v-model="afficherDateFin"
					unique-id="afficherDateFin"
					label="et le"
					type="date"
					:min-date="afficherDateDebutComputed"
					clearable
					:rules="dateRules"
					@input="onDateChange"
					@change="onFilterChange"
				/>
			</v-col>
			<v-col
				v-if="!rechercheDpa"
				cols="12"
				sm="6"
				md="4"
				lg="2"
			>
				<AmeliproSelect
					id="statut"
					v-model="statut"
					bg-white
					label="Statut"
					:items="statuts"
					clearable
					@change="onFilterChange"
				/>
			</v-col>
			<v-col
				v-if="utilisateurExterneInfo.isDelegataire"
				cols="12"
				sm="6"
				md="4"
				lg="2"
			>
				<AmeliproSelect
					id="lot"
					v-model="lot"
					bg-white
					label="Lot"
					:items="lots"
					clearable
					@change="onFilterChange"
				/>
			</v-col>
			<v-col
				v-if="!rechercheDpa && utilisateurExterneInfo.isOfficine"
				cols="12"
				sm="6"
				md="4"
				lg="2"
			>
				<AmeliproTextField
					id="finess"
					v-model="finess"
					bg-white
					label="N° FINESS"
					:rules="finessAmRules"
					clearable
					@change="onFilterChange"
				/>
			</v-col>
			<v-col
				v-if="
					!rechercheDpa &&
						utilisateurExterneInfo.isDocteur &&
						utilisateurExterneInfo.isDelegataire
				"
				cols="12"
				sm="6"
				md="4"
				lg="2"
			>
				<AmeliproTextField
					id="rpps"
					v-model="rpps"
					bg-white
					label="N° RPPS"
					:rules="rppsRules"
					clearable
					@change="onFilterChange"
				/>
			</v-col>
			<v-col
				v-if="
					!rechercheDpa &&
						utilisateurExterneInfo.isDocteur &&
						utilisateurExterneInfo.isDelegataire
				"
				cols="12"
				sm="6"
				md="4"
				lg="2"
			>
				<AmeliproTextField
					id="executant"
					v-model="executant"
					bg-white
					label="Nom"
					clearable
					@change="onFilterChange"
				/>
			</v-col>
			<v-col
				v-if="
					!rechercheDpa &&
						(utilisateurExterneInfo.isDocteur ||
							utilisateurExterneInfo.isOfficine)
				"
				cols="12"
				sm="6"
				md="4"
				lg="2"
			>
				<AmeliproTextField
					id="numeroAm"
					v-model="numeroAm"
					bg-white
					label="N° AM"
					:rules="finessAmRules"
					clearable
					@change="onFilterChange"
				/>
			</v-col>
			<v-col
				v-if="!rechercheDpa"
				cols="12"
				sm="6"
				md="4"
				lg="2"
			>
				<AmeliproTextField
					id="secteur"
					v-model="secteur"
					bg-white
					label="Secteur"
					clearable
					@change="onFilterChange"
				/>
			</v-col>
			<v-col
				v-if="!rechercheDpa"
				cols="12"
				sm="6"
				md="4"
				lg="2"
			>
				<AmeliproSelect
					id="departement"
					v-model="departement"
					bg-white
					label="Département d'exécution"
					:items="departements"
					clearable
					@change="onFilterChange"
				/>
			</v-col>
		</v-row>

		<div
			class="mt-9 d-flex flex-wrap align-center justify-center justify-sm-start flex-sm-row"
		>
			<AmeliproBtn
				class="mr-3 mb-3 mb-sm-0 v-btn-custom"
				icon-name="search"
				@click="rechercherGarde"
			>
				Rechercher

				<template #icon>
					{{ iconMDI }}
				</template>
			</AmeliproBtn>

			<AmeliproBtn
				class="mr-3 mb-3 mb-sm-0 v-btn-custom"
				@click="reinitialiserFiltres"
			>
				Réinitialiser les filtres
			</AmeliproBtn>
		</div>
	</AmeliproCard>
</template>

<script lang="ts">
	import Component, { mixins } from 'vue-class-component';

	import { DataOptions } from 'vuetify';

	import { Garde } from '@/services/api/types';

	import { mdiMagnify } from '@mdi/js';

	import { maxLength } from '@cnamts/vue-dot/src/rules/maxLength';
	import { matchPattern } from '@cnamts/vue-dot/src/rules/matchPattern';

	import { isValidDate } from '@/services/dateFormatter';

	import { getStatutLabel } from '@/services/constantes';

	import UserMixin from '@/mixins/userMixin';
	import { Meta, MetaInfo } from '@/decorators';

	const MixinsDeclaration = mixins(UserMixin);

	@Component<RechercheGarde>({
		props: {
			rechercheDpa: {
				type: Boolean,
				default: false
			}
		},
		computed: {
			afficherDateDebutComputed(): string | null {
				return this.isValidDateDebut() ? this.afficherDateDebut : null;
			},
			afficherDateFinComputed(): string | null {
				return this.isValidDateFin() ? this.afficherDateFin : null;
			}
		}
	})
	export default class RechercheGarde extends MixinsDeclaration {
		rechercheDpa!: boolean;
		@Meta
		metaInfo(): MetaInfo {
			return {
				title: this.rechercheDpa
					? 'PEGASE - Demande de paiement (DPA)'
					: 'PEGASE - Recherche'
			};
		}

		dataOptions = {} as DataOptions;

		iconMDI = mdiMagnify;

		departements: { text: string; value: string }[] = [];
		statuts: { text: string; value: string }[] = [];
		lots: { text: string; value: string }[] = [];

		gardes: Garde[] = [];

		afficherDateDebut: string | null = null;
		afficherDateFin: string | null = null;
		statut: string | null = null; // Valeur par défaut
		lot: string | null = null;
		departement: string | null = null;

		raisonSociale = '';
		rpps = '';
		executant = '';
		finess = '';
		numeroAm = '';
		secteur = '';

		dateDebutKey = 0;
		dateFinKey = 0;

		// Règles de validation
		patternNumeric = new RegExp('^(0|[1-9][0-9]*)$');

		rppsRules = [
			maxLength(11),
			matchPattern(this.patternNumeric, {
				default: 'le N° RPPS doit être une saisie numérique'
			})
		];
		finessAmRules = [
			maxLength(11),
			matchPattern(this.patternNumeric, {
				default: 'le N° AM doit être une saisie numérique'
			})
		];
		dateRules = [maxLength(10)];

		async genereFiltres(): Promise<void> {
			// Récupération des gardes du store issues de la recherche, afin de générer les filtres
			this.gardes = this.$store.getters['garde/getGardes'];
			this.updateSelectOptions();
		}

		updateSelectOptions(): void {
			if (this.gardes) {
				this.statuts = this.getUniqueValues('statutLibelleFront');
				this.lots = this.getUniqueValues('lotPrint');
				this.departements = this.getUniqueValues(
					'gardeRecue.lot.departement.libelleComplet'
				);
			}
		}

		//TODO : déplacer dans mixins
		// Récupère les données du tableau, afin d'alimenter les filtres
		getUniqueValues(attribute: string): { text: string; value: string }[] {
			if (this.gardes) {
				const valuesMap = this.gardes.map((garde) =>
					this.deepValue(garde, attribute)
				);

				const uniqueValuesSet = new Set<string>();

				valuesMap.forEach((value) => {
					if (typeof value === 'string') {
						uniqueValuesSet.add(value);
					}
				});

				return Array.from(uniqueValuesSet).map((text) => ({
					text,
					value: text
				}));
			}
			return [];
		}

		created() {
			this.statut = this.$props.rechercheDpa ? getStatutLabel('a_valider') : null;
		}

		mounted() {
			// Regénère les filtres en cas de nouveaux résultats
			this.$root.$on('resultats-disponibles', this.genereFiltres);
		}

		/**
		 * Récupère les valeurs des champs de recherche
		 */
		rechercherGarde(afficheNotification = true): void {
			// Récupérer les valeurs des champs de recherche
			const filters = {
				afficherDateDebut: this.afficherDateDebut,
				afficherDateFin: this.afficherDateFin,
				statut: this.statut,
				lot: this.lot,
				secteur: this.secteur,
				departement: this.departement,
				raisonSociale: this.raisonSociale,
				executant: this.executant,
				rpps: this.rpps,
				finess: this.finess,
				numeroAm: this.numeroAm
			};
			// Vérifie si tous les éléments de filters sont nuls
			const aucunCritereRenseigne = Object.values(filters).every(
				(value) => value === null || value === ''
			);

			if (aucunCritereRenseigne && afficheNotification) {
				this.$store.dispatch('notification/add', {
					type: 'error',
					message: 'Veuillez renseigner au moins un critère de recherche'
				});
				return;
			}

			// Vérifie si les dates sont correctes
			if (
				(null != this.afficherDateDebut &&
					!isValidDate(this.afficherDateDebut)) ||
				(null != this.afficherDateFin && !isValidDate(this.afficherDateFin))
			) {
				return;
			}

			// Émet l'événement filter-changed avec le payload filters vers le composant Garde qui écoute
			this.$emit('filter-changed', filters);
		}

		reinitialiserFiltres(): void {
			this.statut = this.$props.rechercheDpa ? getStatutLabel('a_valider') : null;
			this.lot = null;
			this.secteur = '';
			this.departement = '';
			this.raisonSociale = '';
			this.executant = '';
			this.numeroAm = '';
			this.finess = '';
			this.rpps = '';
			this.afficherDateDebut = null;
			this.afficherDateFin = null;
			this.onFilterChange();
		}

		onFilterChange(): void {
			this.$emit('afficher-tableau-resultat', false);
			this.$store.dispatch('notification/clear');
		}

		isValidDateDebut(): boolean {
			return isValidDate(this.afficherDateDebut) && null != this.afficherDateFin
				? true
				: false;
		}
		isValidDateFin(): boolean {
			return isValidDate(this.afficherDateFin) && null != this.afficherDateDebut
				? true
				: false;
		}

		onDateChange(): void {
			if (
				this.isValidDateDebut() &&
				this.isValidDateFin() &&
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				this.afficherDateDebut! < this.afficherDateFin!
			) {
				this.dateDebutKey++;
			}
			if (
				this.isValidDateFin() &&
				this.isValidDateDebut() &&
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				this.afficherDateFin! > this.afficherDateDebut!
			) {
				this.dateFinKey++;
			}
		}
	}
</script>
